import type { IEditorVariable } from "@bbj-types/utils";
import {
  BR_TAG_VARIANTS_REGEX,
  GENERIC_SPAN_WRAPPER_REGEX,
  LIST_EACH_GENERIC_END_REGEX,
  LIST_EACH_GENERIC_START_REGEX,
  VAR_CLASS,
  VAR_DATA_ATTR,
  VAR_FIELD_CLASS,
  VAR_RAW_CLASS,
  buildListBlockSpanRegex,
  buildListEachBlockRegex,
  buildListFieldSpanRegex,
  buildRawLabelRegex,
  buildRawVariablePlaceholderRegex,
  buildRawVariableSpanRegex,
  buildSimpleLabelRegex,
  buildSimpleVariablePlaceholderRegex,
  buildSimpleVariableSpanRegex,
} from "./constants";

/**
 * Converts newline-separated content into paragraph tags if the content
 * doesn't already contain paragraphs. Also normalizes any <br> variants to \n first.
 */
const convertInnerToParagraphsIfMissing = (inner: string): string => {
  // If inner already contains paragraphs, keep as-is
  if (/<\s*p[\s>]/i.test(inner)) {
    return inner;
  }
  // Normalize <br> variants to newlines before splitting
  const normalized = inner.replace(BR_TAG_VARIANTS_REGEX, "\n");
  const lines = normalized.split(/\r\n|\r|\n/);
  // Wrap each line in <p>... preserve empty lines
  return lines.map((line) => `<p>${line}</p>`).join("");
};

export const getListEndLabel = (label: string): string => `Fin de la ${label.toLowerCase()}`;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Replaces {{this.field}} placeholders in list block inner content with field spans
 */
const replaceFieldPlaceholdersInListContent = (inner: string, fields: IEditorVariable[]): string => {
  if (fields.length === 0) {
    return inner;
  }

  const fieldLookup = new Map(fields.map((field) => [field.value, field]));
  const fieldPattern = fields.map((field) => escapeRegExp(field.value)).join("|");
  const rawFieldRegex = new RegExp(`\\{\\{\\{this\\.(${fieldPattern})\\}\\}\\}`, "g");
  const combinedFieldRegex = new RegExp(`(?<!\\{)\\{\\{this\\.(${fieldPattern})\\}\\}(?!\\})`, "g");
  const toFieldSpan = (isRaw: boolean) => (placeholder: string, fieldValue: string) => {
    const field = fieldLookup.get(fieldValue);
    if (!field) {
      return placeholder;
    }
    return createFieldSpan(field.value, field.label, isRaw);
  };

  return inner.replace(rawFieldRegex, toFieldSpan(true)).replace(combinedFieldRegex, toFieldSpan(false));
};

const withRawClass = (className: string, isRaw: boolean): string =>
  isRaw ? `${className} ${VAR_RAW_CLASS}` : className;

/**
 * Creates a span element for a simple variable
 */
export const createSimpleVariableSpan = (label: string, value: string, isRaw = false): string => {
  const text = `[${label}]`;
  return `<span class="${withRawClass(VAR_CLASS, isRaw)}" contenteditable="false" ${VAR_DATA_ATTR}="${value}">${text}</span>`;
};

/**
 * Creates a span element for a list field variable
 */
export const createFieldSpan = (fieldValue: string, fieldLabel: string, isRaw = false): string => {
  return `<span class="${withRawClass(VAR_FIELD_CLASS, isRaw)}" contenteditable="false" ${VAR_DATA_ATTR}="${fieldValue}">[${fieldLabel}]</span>`;
};

/**
 * Creates a span element for a list start marker
 */
export const createListStartSpan = (listValue: string, label: string): string => {
  return `<span class="${VAR_CLASS}" contenteditable="false" ${VAR_DATA_ATTR}="${listValue}">[${label}]</span>`;
};

/**
 * Creates a span element for a list end marker
 */
export const createListEndSpan = (listValue: string, label: string): string => {
  const endLabel = getListEndLabel(label);
  return `<span class="${VAR_CLASS}" contenteditable="false" ${VAR_DATA_ATTR}="${listValue}-end">[${endLabel}]</span>`;
};

/**
 * Creates span structure for list variables with template support
 */
export const createListVariableSpan = (lv: IEditorVariable): string => {
  if (lv.defaultTemplate) {
    // Use the default template and replace variables with spans
    let template = lv.defaultTemplate;

    // Replace {{this.fieldName}} with individual field spans
    if (!lv.variables) {
      return "";
    }
    template = replaceFieldPlaceholdersInListContent(template, lv.variables);

    // Replace the {{#each listName}}...{{/each}} wrapper with our spans
    const startSpan = createListStartSpan(lv.value, lv.label);
    const endSpan = createListEndSpan(lv.value, lv.label);

    // Remove the {{#each}} and {{/each}} tags and wrap with our spans
    template = template.replace(LIST_EACH_GENERIC_START_REGEX, startSpan);
    template = template.replace(LIST_EACH_GENERIC_END_REGEX, endSpan);

    // Convert inner content between our start/end spans into paragraphs if missing
    template = convertInnerToParagraphsIfMissing(template);

    return template;
  }

  // Fallback to the old format if no default template
  if (!lv.variables) {
    return "";
  }
  const fieldsList = lv.variables.map((v: IEditorVariable) => createFieldSpan(v.value, v.label)).join(", ");
  const startSpan = createListStartSpan(lv.value, lv.label);
  const endSpan = createListEndSpan(lv.value, lv.label);
  return `${startSpan} : <br>${fieldsList}<br>${endSpan}`;
};

/**
 * Transforms raw variables in content to styled spans
 */
export const transformRawVariablesToSpans = (content: string, variableList?: IEditorVariable[]): string => {
  let processedValue = content;

  if (!variableList || variableList.length === 0) {
    return processedValue;
  }

  // 1. Process all variables by {{value}} format
  for (const v of variableList) {
    if (v.variables) {
      // This is a list variable
      const regex = buildListEachBlockRegex(v.value);
      const fields = v.variables || [];

      processedValue = processedValue.replace(regex, (_match: string, inner: string) => {
        let innerWithFieldSpans = replaceFieldPlaceholdersInListContent(inner, fields);

        // Convert newline-separated inner content to paragraphs if none exist
        innerWithFieldSpans = convertInnerToParagraphsIfMissing(innerWithFieldSpans);
        const startSpan = createListStartSpan(v.value, v.label);
        const endSpan = createListEndSpan(v.value, v.label);
        return `${startSpan}${innerWithFieldSpans}${endSpan}`;
      });
    } else {
      // This is a simple variable
      const rawRegex = buildRawVariablePlaceholderRegex(v.value);
      processedValue = processedValue.replace(rawRegex, createSimpleVariableSpan(v.label, v.value, true));
      const regex = buildSimpleVariablePlaceholderRegex(v.value);
      processedValue = processedValue.replace(regex, createSimpleVariableSpan(v.label, v.value));
    }
  }

  // 2. Also detect variables by [label] format (manually typed)
  for (const v of variableList) {
    if (!v.variables) {
      continue;
    }

    const startRegex = buildSimpleLabelRegex(v.label);
    const startSpan = createListStartSpan(v.value, v.label);
    processedValue = processedValue.replace(startRegex, startSpan);

    const endLabel = getListEndLabel(v.label);
    const endRegex = buildSimpleLabelRegex(endLabel);
    const endSpan = createListEndSpan(v.value, v.label);
    processedValue = processedValue.replace(endRegex, endSpan);

    for (const field of v.variables) {
      const rawFieldRegex = buildRawLabelRegex(field.label);
      processedValue = processedValue.replace(rawFieldRegex, createFieldSpan(field.value, field.label, true));
      const fieldRegex = buildSimpleLabelRegex(field.label);
      const fieldSpan = createFieldSpan(field.value, field.label);
      processedValue = processedValue.replace(fieldRegex, fieldSpan);
    }
  }

  // 3. Also detect simple variables typed as [label]
  for (const v of variableList) {
    if (!v.variables) {
      // Only simple variables
      const rawLabelRegex = buildRawLabelRegex(v.label);
      processedValue = processedValue.replace(rawLabelRegex, createSimpleVariableSpan(v.label, v.value, true));
      const labelRegex = buildSimpleLabelRegex(v.label);

      processedValue = processedValue.replace(labelRegex, createSimpleVariableSpan(v.label, v.value));
    }
  }

  return processedValue;
};

/**
 * Serializes editor HTML by converting variable spans back to raw Handlebars-style variables.
 * - Simple variables: <span class="bbj-var" data-bbj-var="name">[Label]</span> -> {{name}}
 * - Lists: span(data-bbj-var=list) ... span(data-bbj-var=list-end) with inner
 *   <span class="bbj-var-field" data-bbj-var="field">[Label]</span> ->
 *   {{#each list}} ... {{/each}} where inner fields become {{this.field}}
 */
export const spansToRawVariables = (html: string, variableList?: IEditorVariable[]): string => {
  let output = html;

  if (!variableList || variableList.length === 0) {
    return output;
  }

  // Process all variables, detecting list vs simple by presence of variables field
  for (const v of variableList) {
    if (v.variables) {
      // This is a list variable: from spans back to {{#each list}} ... {{/each}}
      const blockRegex = buildListBlockSpanRegex(v.value);

      output = output.replace(blockRegex, (_match, inner: string) => {
        // Replace field spans within the inner content back to {{this.field}}
        let innerSerialized = inner;
        if (!v.variables) {
          return `{{#each ${v.value}}}${inner}{{/each}}`;
        }
        for (const variable of v.variables) {
          const rawFieldSpanRegex = buildRawVariableSpanRegex(variable.value);
          innerSerialized = innerSerialized.replace(rawFieldSpanRegex, `{{{this.${variable.value}}}}`);
          const fieldSpanRegex = buildListFieldSpanRegex(variable.value);
          innerSerialized = innerSerialized.replace(fieldSpanRegex, `{{this.${variable.value}}}`);
        }
        // Return full each block with inner content preserved
        return `{{#each ${v.value}}}${innerSerialized}{{/each}}`;
      });
    } else {
      // This is a simple variable: back to {{var}}
      const rawSpanRegex = buildRawVariableSpanRegex(v.value);
      output = output.replace(rawSpanRegex, `{{{${v.value}}}}`);
      const simpleSpanRegex = buildSimpleVariableSpanRegex(v.value);
      output = output.replace(simpleSpanRegex, `{{${v.value}}}`);
    }
  }

  // Clean up any editor artefacts we don't want in outbound value
  // Convert <br> variants to \n to preserve intended line breaks in templates
  output = output.replace(BR_TAG_VARIANTS_REGEX, "\n");
  // Strip remaining span wrappers if any slipped through (keep inner text)
  output = output.replace(GENERIC_SPAN_WRAPPER_REGEX, "$1");
  return output;
};
